import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import { es } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { API } from "@/config";
import ServicePicker, { itemsTotal } from "@/components/site/ServicePicker";
import { toKey, fmtDate } from "@/components/admin/useAppointments";

const inputCls =
  "w-full rounded-xl border border-sand bg-cream px-4 py-2.5 text-sm text-obsidian placeholder:text-taupe/60 focus:outline-none focus:ring-2 focus:ring-gold";

export default function EditAppointmentDialog({ appointment: a, open, onClose, onSaved, headers }) {
  const [services, setServices] = useState([]);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", email: "", price: "", location: "estudio", address: "" });
  const [date, setDate] = useState(null);
  const [avail, setAvail] = useState(null);
  const [time, setTime] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !a) return;
    setForm({
      name: a.name || "",
      phone: a.phone || "",
      email: a.email || "",
      price: a.price != null ? String(a.price) : "",
      location: a.location || "estudio",
      address: a.address || "",
    });
    setItems((a.items || []).map((it) => ({ name: it.name, qty: it.qty })));
    setDate(new Date(a.date + "T12:00:00"));
    setTime(a.time);
    setAvail(null);
    axios.get(`${API}/services`).then((r) => setServices(r.data)).catch(() => {});
  }, [open, a?.id]);

  useEffect(() => {
    if (open && date) {
      axios.get(`${API}/appointments/availability?date=${toKey(date)}`).then((r) => setAvail(r.data)).catch(() => {});
    }
  }, [open, date && toKey(date)]);

  if (!a) return null;

  const changeItems = (next) => {
    setItems(next);
    setForm((f) => ({ ...f, price: next.length ? String(itemsTotal(services, next)) : "" }));
  };

  const ready =
    form.name.trim() &&
    form.phone.trim() &&
    items.length > 0 &&
    date &&
    /^\d{2}:\d{2}$/.test(time) &&
    (form.location === "estudio" || form.address.trim().length >= 5);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/appointments/${a.id}/edit`,
        { ...form, items, price: form.price === "" ? null : Number(form.price), date: toKey(date), time },
        { headers }
      );
      toast.success("Cita actualizada");
      onSaved(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar la cita");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-appointment-dialog" data-lenis-prevent className="bg-creamcard border-sand max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-obsidian">Editar cita</DialogTitle>
          <DialogDescription className="text-taupe">
            Cita original: {fmtDate(a.date)} a las {a.time}. Puedes añadir o quitar servicios y cambiar el día, la hora o el lugar. No se enviará ningún email a la clienta.
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-3">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre de la clienta" data-testid="edit-appt-name" className={inputCls} />
          <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Teléfono" data-testid="edit-appt-phone" className={inputCls} />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Correo (opcional)" data-testid="edit-appt-email" className={`${inputCls} sm:col-span-2`} />
        </div>

        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Servicios</p>
          <ServicePicker services={services} items={items} onChange={changeItems} testPrefix="edit-appt" />
          <label className="flex items-center gap-2 mt-3 text-sm text-cocoa">
            Importe
            <input type="number" min="0" step="0.5" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="edit-appt-price" className="w-24 rounded-xl border border-sand bg-cream px-3 py-1.5 text-sm" />
            €
          </label>
        </div>

        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">¿Dónde?</p>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {[{ id: "estudio", label: "En mi estudio" }, { id: "domicilio", label: "En su domicilio" }].map((o) => (
              <button
                key={o.id}
                onClick={() => setForm({ ...form, location: o.id })}
                data-testid={`edit-appt-location-${o.id}`}
                className={`rounded-xl border px-3 py-2 text-sm transition-colors ${
                  form.location === o.id ? "border-gold bg-rosesoft text-obsidian" : "border-sand bg-cream text-cocoa hover:border-gold/50"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          {form.location === "domicilio" && (
            <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Dirección completa de la clienta" rows={2} data-testid="edit-appt-address" className={`${inputCls} mt-2`} />
          )}
        </div>

        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Día y hora</p>
          <div className="mt-2 flex justify-center rounded-xl border border-sand bg-cream">
            <Calendar mode="single" locale={es} selected={date} onSelect={(d) => d && setDate(d)} data-testid="edit-appt-calendar" />
          </div>
          <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-2">
            {(avail?.slots || []).map((s) => {
              const busy = avail.taken.includes(s) && s !== a.time;
              return (
                <button
                  key={s}
                  disabled={busy}
                  onClick={() => setTime(s)}
                  data-testid={`edit-appt-slot-${s.replace(":", "")}`}
                  className={`rounded-lg border px-2 py-1.5 text-sm transition-colors ${
                    busy ? "border-sand bg-sand/40 text-taupe/50 line-through cursor-not-allowed" : time === s ? "border-gold bg-gold text-white" : "border-sand bg-cream text-cocoa hover:border-gold/60"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
          <label className="flex items-center gap-2 mt-3 text-sm text-cocoa">
            Hora:
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="edit-appt-time" className="rounded-xl border border-sand bg-cream px-3 py-1.5 text-sm" />
          </label>
        </div>

        <button
          onClick={submit}
          disabled={!ready || saving}
          data-testid="edit-appt-submit"
          className="w-full rounded-xl bg-gold hover:bg-gold-dark disabled:opacity-50 text-white text-sm font-medium py-3 transition-colors flex items-center justify-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Guardar cambios
        </button>
      </DialogContent>
    </Dialog>
  );
}
